import React, { useState, useEffect, useRef } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
    Calendar, Users, Plus, Edit2, Trash2,
    LayoutDashboard, Settings, LogOut, Search, Building2, Mail, Clock, Image, Tag, Building, Menu, X,
    Bell, ClipboardList, MapPin, Globe
} from 'lucide-react';
import { format } from 'date-fns';
import api from '../../../api';
import AdminPartners from '../../../components/admin/AdminPartners';
import AdminContacts from './AdminContacts';
import AdminUsers from './AdminUsers';
import AdminPendingEvents from './AdminPendingEvents';
import AdminBanners from '../../../components/admin/AdminBanners';
import AdminCategories from '../../../components/admin/AdminCategories';
import AdminNotifications from '../../../components/admin/AdminNotifications';
import AdminAuditLog from '../../../components/admin/AdminAuditLog';
import AdminDashboardCalendar from '../../../components/admin/AdminDashboardCalendar';
import AlertModal from '../../../components/AlertModal';
import './AdminPortal.css';

export default function AdminPage() {
    const { user, isAdmin, logout, loading: authLoading, showAlert } = useAuth();
    const [activeTab, setActiveTab] = useState('dashboard');
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [pendingCount, setPendingCount] = useState(0);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [notifCount, setNotifCount] = useState(0);
    const [showNotifDrop, setShowNotifDrop] = useState(false);
    const [recentNotifs, setRecentNotifs] = useState([]);
    const notifRef = useRef(null);

    const refreshAll = async () => {
        setLoading(true);
        await Promise.all([
            loadEvents(),
            loadPendingCount(),
            loadNotifCount()
        ]);
        setLoading(false);
    };

    useEffect(() => {
        if (isAdmin) {
            refreshAll();
        }
    }, [isAdmin]);

    // Poll notifications every 30s
    useEffect(() => {
        if (!isAdmin) return;
        const interval = setInterval(loadNotifCount, 30000);
        return () => clearInterval(interval);
    }, [isAdmin]);

    // Close notif dropdown when clicking outside
    useEffect(() => {
        const handler = (e) => {
            if (notifRef.current && !notifRef.current.contains(e.target)) {
                setShowNotifDrop(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const loadNotifCount = async () => {
        try {
            const data = await api.getAdminNotifications();
            const unread = data.filter(n => !n.read);
            setNotifCount(unread.length);
            setRecentNotifs(data.slice(0, 5));
        } catch { }
    };

    const loadPendingCount = async () => {
        try {
            const count = await api.getPendingCount();
            setPendingCount(count);
        } catch { }
    };

    const loadEvents = async () => {
        try {
            const data = await api.getEvents({ limit: 100 });
            setEvents(data);
        } catch (error) {
            console.error('Error loading events:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (eventId) => {
        showAlert(
            'Delete Event',
            'Are you sure you want to delete this event?',
            'warning',
            async () => {
                try {
                    await api.deleteEvent(eventId);
                    setEvents(events.filter(e => e.id !== eventId));
                    showAlert('Success', 'Event deleted successfully.', 'success');
                } catch (error) {
                    showAlert('Error', 'Error deleting event: ' + error.message, 'error');
                }
            },
            true, // show cancel
            'Delete'
        );
    };

    if (authLoading) {
        return (
            <main className="main">
                <div className="skeleton" style={{ height: '100vh' }} />
            </main>
        );
    }

    if (!user || !isAdmin) {
        return <Navigate to="/" replace />;
    }

    const filteredEvents = events.filter(e =>
        e.title.toLowerCase().includes(search.toLowerCase()) ||
        e.category.toLowerCase().includes(search.toLowerCase())
    );

    const toggleMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
    const closeMenu = () => setIsMobileMenuOpen(false);

    return (
        <div className="admin-portal-v2">
            {/* NEW TOP NAVIGATION */}
            <header className="admin-v2-header">
                <div className="admin-v2-header-left">
                    <div className="admin-v2-logo">
                        <LayoutDashboard size={24} color="#6366F1" />
                        <span>ATAM Admin</span>
                    </div>
                </div>

                <nav className="admin-v2-tabs">
                    {[
                        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
                        { id: 'events', label: 'Events', icon: Calendar },
                        { id: 'partners', label: 'Partners', icon: Building2 },
                        { id: 'pending', label: 'Pending', icon: Clock, badge: pendingCount },
                        { id: 'partner-accounts', label: 'Accounts', icon: Building },
                        { id: 'users', label: 'Users', icon: Users },
                        { id: 'notifications', label: 'Inbox', icon: Bell, badge: notifCount },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            className={`admin-v2-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <tab.icon size={18} />
                            {tab.label}
                            {tab.badge > 0 && <span className="admin-v2-badge">{tab.badge}</span>}
                        </button>
                    ))}
                </nav>

                <div className="admin-v2-header-right">
                    <div className="admin-v2-search">
                        <Search size={18} />
                        <input type="text" placeholder="Global search..." />
                    </div>
                    
                    <div className="admin-v2-user" ref={notifRef}>
                        <div className="admin-v2-avatar">
                            {user.name?.charAt(0) || 'A'}
                        </div>
                        <div className="admin-v2-user-info">
                            <span className="admin-v2-user-name">{user.name || 'Admin'}</span>
                            <span className="admin-v2-user-role">Master Admin</span>
                        </div>
                    </div>
                    
                    <button className="admin-v2-logout" onClick={logout} title="Logout">
                        <LogOut size={20} />
                    </button>
                </div>
            </header>

            <div className="admin-v2-main">
                {/* SECONDARY SIDEBAR - Workspace/Filters */}
                <aside className="admin-v2-sidebar">
                    <div className="admin-v2-sidebar-section">
                        <h4 className="admin-v2-sidebar-title">Management</h4>
                        <div className="admin-v2-sidebar-links">
                            <button className={`admin-v2-side-btn ${activeTab === 'banners' ? 'active' : ''}`} onClick={() => setActiveTab('banners')}>
                                <Image size={16} /> Banners
                            </button>
                            <button className={`admin-v2-side-btn ${activeTab === 'categories' ? 'active' : ''}`} onClick={() => setActiveTab('categories')}>
                                <Tag size={16} /> Categories
                            </button>
                            <button className={`admin-v2-side-btn ${activeTab === 'contacts' ? 'active' : ''}`} onClick={() => setActiveTab('contacts')}>
                                <Mail size={16} /> Contacts
                            </button>
                            {/* 
                            <button className={`admin-v2-side-btn ${activeTab === 'audit' ? 'active' : ''}`} onClick={() => setActiveTab('audit')}>
                                <ClipboardList size={16} /> Audit Log
                            </button> 
                            */}
                            <button className={`admin-v2-side-btn ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
                                <Settings size={16} /> Settings
                            </button>
                        </div>
                    </div>

                    {activeTab === 'events' && (
                        <div className="admin-v2-sidebar-section">
                            <h4 className="admin-v2-sidebar-title">Filters</h4>
                            <div className="admin-v2-filter-group">
                                <label><input type="checkbox" defaultChecked /> Art Performance</label>
                                <label><input type="checkbox" defaultChecked /> Workshop</label>
                                <label><input type="checkbox" defaultChecked /> Exhibition</label>
                            </div>
                        </div>
                    )}
                </aside>

                <div className="admin-v2-content">
                    {activeTab === 'partners' ? (
                    <AdminPartners />
                ) : activeTab === 'pending' ? (
                    <AdminPendingEvents onStatusChange={refreshAll} />
                ) : activeTab === 'dashboard' ? (
                    <DashboardTab events={events} pendingCount={pendingCount} setActiveTab={setActiveTab} refreshAll={refreshAll} />
                ) : activeTab === 'events' ? (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
                            <div>
                                <h1 className="admin-page-title" style={{ marginBottom: '8px' }}>Events Management</h1>
                                <p style={{ color: 'var(--v2-text-muted)' }}>
                                    Manage, create, and organize events
                                </p>
                            </div>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <Link 
                                    to="/admin/events/create" 
                                    style={{ 
                                        display: 'flex', alignItems: 'center', gap: '8px',
                                        padding: '10px 20px', borderRadius: '12px',
                                        background: 'var(--v2-primary)', border: 'none',
                                        color: 'white', fontWeight: '600', fontSize: '14px',
                                        cursor: 'pointer', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
                                        textDecoration: 'none'
                                    }}
                                >
                                    <Plus size={18} /> Add Event
                                </Link>
                            </div>
                        </div>

                        {/* Stats */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '24px',
                            marginBottom: '40px'
                        }}>
                            <div className="admin-card-premium">
                                <div className="admin-stat-title">Total Events</div>
                                <div className="admin-stat-value" style={{ color: 'var(--admin-primary)' }}>
                                    {events.length}
                                </div>
                            </div>
                            <div className="admin-card-premium">
                                <div className="admin-stat-title">Booking Clicks</div>
                                <div className="admin-stat-value" style={{ color: 'var(--admin-secondary)' }}>
                                    {events.reduce((sum, e) => sum + (e.booking_clicks || 0), 0)}
                                </div>
                            </div>
                            <div className="admin-card-premium">
                                <div className="admin-stat-title">Categories</div>
                                <div className="admin-stat-value" style={{ color: 'var(--admin-accent)' }}>
                                    {new Set(events.map(e => e.category)).size}
                                </div>
                            </div>
                        </div>

                        {/* Search */}
                        <div className="admin-search-wrapper" style={{ marginBottom: '24px' }}>
                            <Search size={18} className="admin-search-icon" />
                            <input
                                type="text"
                                className="admin-search-input"
                                placeholder="Search events by title or category..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>

                        {/* Events Table */}
                        <div className="admin-table-container">
                            {loading ? (
                                <div className="skeleton" style={{ height: '400px' }} />
                            ) : (
                                <table className="admin-table-premium">
                                    <thead>
                                        <tr>
                                            <th>Event</th>
                                            <th>Date</th>
                                            <th>Category</th>
                                            <th>Capacity</th>
                                            <th>Price</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredEvents.map((event) => (
                                            <tr key={event.id}>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                                        <img
                                                            src={event.image_url || 'https://via.placeholder.com/50'}
                                                            alt=""
                                                            style={{
                                                                width: '56px',
                                                                height: '56px',
                                                                borderRadius: '12px',
                                                                objectFit: 'cover',
                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                                            }}
                                                        />
                                                        <div>
                                                            <div style={{ fontWeight: '700', fontSize: '15px', color: 'var(--v2-text-main)' }}>{event.title}</div>
                                                            <div style={{ fontSize: '12px', color: 'var(--v2-text-muted)', marginTop: '2px' }}>
                                                                {event.location}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ fontWeight: '600', fontSize: '14px', color: 'var(--v2-text-main)' }}>
                                                        {format(new Date(event.date), 'MMM d, yyyy')}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="status-tag category">{event.category}</span>
                                                </td>
                                                <td>
                                                    <div style={{ fontSize: '14px', fontWeight: '500' }}>{event.capacity ? `${event.capacity} seats` : 'Unlimited'}</div>
                                                </td>
                                                <td>
                                                    <div style={{ fontWeight: '700', color: 'var(--v2-primary)', fontSize: '15px' }}>
                                                        {event.price ? `$${event.price}` : 'Free'}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="table-actions">
                                                        <Link
                                                            to={`/admin/events/edit/${event.id}`}
                                                            className="table-action-btn edit"
                                                            title="Edit Event"
                                                        >
                                                            <Edit2 size={18} />
                                                        </Link>
                                                        <button
                                                            className="table-action-btn delete"
                                                            onClick={() => handleDelete(event.id)}
                                                            title="Delete Event"
                                                        >
                                                            <Trash2 size={18} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </>
                ) : activeTab === 'banners' ? (
                    <AdminBanners />
                ) : activeTab === 'categories' ? (
                    <AdminCategories />
                ) : activeTab === 'contacts' ? (
                    <AdminContacts />
                ) : activeTab === 'users' ? (
                    <AdminUsers />
                ) : activeTab === 'partner-accounts' ? (
                    <PartnerAccountsTab />
                ) : activeTab === 'notifications' ? (
                    <AdminNotifications />
                ) : activeTab === 'audit' ? (
                    <AdminAuditLog />
                ) : activeTab === 'settings' ? (
                    <SettingsTab />
                ) : (
                    <div className="admin-card-premium" style={{ padding: '48px', textAlign: 'center' }}>
                        <p style={{ color: 'var(--admin-text-muted)' }}>Select a tab from the sidebar</p>
                    </div>
                )}
                </div>
            </div>
        </div>
    );
}

// Dashboard Tab Component
function DashboardTab({ events, pendingCount, setActiveTab }) {
    const [recentPending, setRecentPending] = useState([]);
    const [loadingPending, setLoadingPending] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState(null);

    useEffect(() => {
        const loadRecentPending = async () => {
            try {
                const data = await api.getPendingEvents();
                setRecentPending(data.slice(0, 3)); 
            } catch (error) {
                console.error('Failed to load pending events', error);
            } finally {
                setLoadingPending(false);
            }
        };
        loadRecentPending();
    }, []);

    return (
        <div>
            <div className="admin-page-header">
                <h1 className="admin-page-title">Dashboard Overview</h1>
            </div>

            {/* Stats Row */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '24px',
                marginBottom: '40px'
            }}>
                <div className="admin-card-premium">
                    <div className="admin-stat-title">Total Events</div>
                    <div className="admin-stat-value" style={{ color: 'var(--admin-primary)' }}>
                        {events.length}
                    </div>
                </div>
                <div className="admin-card-premium">
                    <div className="admin-stat-title">Booking Clicks</div>
                    <div className="admin-stat-value" style={{ color: '#8b5cf6' }}>
                        {events.reduce((sum, e) => sum + (e.booking_clicks || 0), 0)}
                    </div>
                </div>
                <div className="admin-card-premium">
                    <div className="admin-stat-title">Categories</div>
                    <div className="admin-stat-value" style={{ color: 'var(--admin-accent)' }}>
                        {new Set(events.map(e => e.category)).size}
                    </div>
                </div>
                <div className="admin-card-premium">
                    <div className="admin-stat-title">Upcoming</div>
                    <div className="admin-stat-value" style={{ color: 'var(--admin-success)' }}>
                        {events.filter(e => new Date(e.date) > new Date()).length}
                    </div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '40px' }}>
                {/* Pending Approvals Section */}
                <div className="admin-card-premium">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h3 style={{ fontSize: '1.2rem', fontWeight: 600, color: 'var(--admin-text-main)' }}>
                            Recent Pending Approvals
                        </h3>
                        {pendingCount > 0 && (
                            <button 
                                onClick={() => setActiveTab('pending')}
                                style={{ 
                                    background: 'none', border: 'none', color: 'var(--admin-primary)', 
                                    fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem' 
                                }}
                            >
                                View All ({pendingCount})
                            </button>
                        )}
                    </div>

                    {loadingPending ? (
                        <div className="skeleton" style={{ height: '100px', borderRadius: '12px' }}></div>
                    ) : recentPending.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '30px', background: 'rgba(16, 185, 129, 0.05)', borderRadius: '12px' }}>
                            <p style={{ color: '#10b981', fontWeight: 500 }}>All caught up! No pending events.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {recentPending.map(event => (
                                <div key={event.id} style={{ 
                                    display: 'flex', alignItems: 'center', gap: '16px', 
                                    padding: '12px', border: '1px solid var(--border)', 
                                    borderRadius: '12px', background: 'var(--bg-secondary)' 
                                }}>
                                    <img 
                                        src={event.image_url || 'https://via.placeholder.com/50'} 
                                        alt="" 
                                        style={{ width: '48px', height: '48px', borderRadius: '8px', objectFit: 'cover' }}
                                    />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 600, color: 'var(--admin-text-main)', fontSize: '0.95rem' }}>{event.title}</div>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>
                                            {event.submitter_name || 'Partner'} • {event.category}
                                        </div>
                                    </div>
                                    <button 
                                        onClick={() => setActiveTab('pending')}
                                        className="btn-premium btn-premium-secondary"
                                        style={{ padding: '6px 16px', fontSize: '0.85rem' }}
                                    >
                                        Review
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Calendar Grid Section */}
                <div style={{ marginBottom: '40px' }}>
                    <h3 style={{ fontSize: '1.2rem', fontWeight: 600, color: 'var(--admin-text-main)', marginBottom: '20px' }}>
                        Platform Schedule
                    </h3>
                    <AdminDashboardCalendar events={events} onEventClick={(evt) => setSelectedEvent(evt)} />
                </div>
            </div>

            {/* Event Drawer */}
            {selectedEvent && (
                <>
                    <div 
                        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99998 }}
                        onClick={() => setSelectedEvent(null)}
                    />
                    <div 
                        style={{
                            position: 'fixed', top: 0, right: 0, bottom: 0, width: '400px', maxWidth: '100%',
                            background: 'var(--bg-card)', zIndex: 99999,
                            boxShadow: '-4px 0 24px rgba(0,0,0,0.1)', overflowY: 'auto',
                            transition: 'transform 0.3s ease-in-out',
                            transform: 'translateX(0)'
                        }}
                    >
                        {selectedEvent.image_url ? (
                            <div style={{ width: '100%', height: '200px', backgroundImage: `url(${selectedEvent.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative' }}>
                                <button
                                    onClick={() => setSelectedEvent(null)}
                                    style={{ position: 'absolute', top: '16px', right: '16px', background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        ) : (
                            <div style={{ padding: '24px 24px 0 24px', display: 'flex', justifyContent: 'flex-end' }}>
                                <button
                                    onClick={() => setSelectedEvent(null)}
                                    style={{ background: 'transparent', color: 'var(--text-secondary)', border: 'none', cursor: 'pointer' }}
                                >
                                    <X size={24} />
                                </button>
                            </div>
                        )}
                        <div style={{ padding: '24px' }}>
                            <div style={{ display: 'inline-block', padding: '4px 12px', background: 'var(--bg-secondary)', color: 'var(--text-secondary)', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '12px' }}>
                                {selectedEvent.category || 'Event'}
                            </div>
                            <h2 style={{ fontSize: '1.5rem', fontWeight: '800', marginBottom: '8px', color: 'var(--text-primary)' }}>{selectedEvent.title}</h2>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '24px', lineHeight: '1.5' }}>
                                {selectedEvent.description || 'No description provided.'}
                            </p>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)' }}>
                                        <Calendar size={20} />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', fontWeight: '600' }}>Date & Time</div>
                                        <div style={{ fontSize: '0.9rem', color: 'var(--text-primary)', fontWeight: '500' }}>
                                            {format(new Date(selectedEvent.date), 'MMM d, yyyy • h:mm a')}
                                        </div>
                                    </div>
                                </div>
                                
                                {selectedEvent.location && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)' }}>
                                            <MapPin size={20} />
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', fontWeight: '600' }}>Location</div>
                                            <div style={{ fontSize: '0.9rem', color: 'var(--text-primary)', fontWeight: '500' }}>
                                                {selectedEvent.location}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)' }}>
                                        <Users size={20} />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', fontWeight: '600' }}>Organizer</div>
                                        <div style={{ fontSize: '0.9rem', color: 'var(--text-primary)', fontWeight: '500' }}>
                                            {selectedEvent.organizer || 'Unknown'}
                                        </div>
                                    </div>
                                </div>
                                
                                {selectedEvent.external_booking_url && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)' }}>
                                            <Globe size={20} />
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', fontWeight: '600' }}>Booking Link</div>
                                            <a href={selectedEvent.external_booking_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem', color: 'var(--primary)', fontWeight: '500', textDecoration: 'none' }}>
                                                External Details ↗
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

// Partner Accounts Tab
function PartnerAccountsTab() {
    const { showAlert } = useAuth();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [loadingAccounts, setLoadingAccounts] = useState(true);

    useEffect(() => {
        loadAccounts();
    }, []);

    const loadAccounts = async () => {
        try {
            setLoadingAccounts(true);
            const data = await api.getAllUsers();
            // Filter only partners
            setAccounts(data.filter(u => u.is_partner || u.role === 'partner'));
        } catch (err) {
            console.error('Failed to load accounts:', err);
        } finally {
            setLoadingAccounts(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.createPartnerAccount({ name, email, password });
            showAlert('Success', `Partner account created: ${email}`, 'success');
            setName(''); setEmail(''); setPassword('');
            loadAccounts(); // Refresh list
        } catch (err) {
            showAlert('Creation Failed', err.message || 'Failed to create account', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const inputStyle = {
        width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border)',
        background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 15, boxSizing: 'border-box'
    };

    return (
        <div>
            <div className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">Partner Accounts</h1>
                    <p style={{ color: 'var(--admin-text-muted)', marginTop: 4 }}>
                        Manage login credentials for partner organizations
                    </p>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start', marginBottom: 32 }}>
                {/* Create Account Form */}
                <div className="admin-card-premium" style={{ height: '100%' }}>
                    <h3 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 8, color: 'var(--admin-text-main)' }}>Create Partner Login</h3>
                    <p style={{ color: 'var(--admin-text-muted)', fontSize: '0.95rem', marginBottom: 24 }}>
                        Partners use these credentials to log in and submit events.
                    </p>

                    <form onSubmit={handleCreate}>
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Partner / Org Name *</label>
                            <input value={name} onChange={e => setName(e.target.value)} required placeholder="e.g. Tamil Cultural Association" style={inputStyle} />
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Email *</label>
                            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="partner@org.sg" style={inputStyle} />
                        </div>
                        <div style={{ marginBottom: 24 }}>
                            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Password *</label>
                            <input value={password} onChange={e => setPassword(e.target.value)} required placeholder="Min 8 characters" style={inputStyle} />
                        </div>
                        <button type="submit" disabled={submitting} style={{ 
                            width: '100%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                            padding: '10px 20px', borderRadius: '12px',
                            background: 'var(--v2-primary)', border: 'none',
                            color: 'white', fontWeight: '600', fontSize: '14px',
                            cursor: 'pointer', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)'
                        }}>
                            {submitting ? 'Creating...' : '+ Create Partner Account'}
                        </button>
                    </form>
                </div>

                {/* Account List */}
                <div className="admin-card-premium" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h3 style={{ fontSize: '1.2rem', fontWeight: 700, margin: 0, color: 'var(--admin-text-main)' }}>Existing Accounts</h3>
                        <span style={{ fontSize: '0.85rem', color: 'var(--admin-text-muted)', background: 'var(--admin-sidebar-active-bg)', padding: '2px 8px', borderRadius: 12 }}>{accounts.length}</span>
                    </div>

                    {loadingAccounts ? (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
                             <div style={{ width: 24, height: 24, border: '2px solid var(--admin-primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'admin-spin 0.8s linear infinite' }} />
                        </div>
                    ) : accounts.length === 0 ? (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, textAlign: 'center' }}>
                            <p style={{ color: 'var(--admin-text-muted)' }}>No partner accounts yet.</p>
                        </div>
                    ) : (
                        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead style={{ position: 'sticky', top: 0, background: 'var(--admin-card-bg)', zIndex: 1 }}>
                                    <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--admin-border)' }}>
                                        <th style={{ padding: '12px 8px', fontSize: 13, color: 'var(--admin-text-muted)' }}>Partner</th>
                                        <th style={{ padding: '12px 8px', fontSize: 13, color: 'var(--admin-text-muted)' }}>Created</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {accounts.map(acc => (
                                        <tr key={acc.id} style={{ borderBottom: '1px solid var(--admin-border-light)' }}>
                                            <td style={{ padding: '12px 8px' }}>
                                                <div style={{ fontWeight: 600, fontSize: 14 }}>{acc.name}</div>
                                                <div style={{ fontSize: 12, color: 'var(--admin-text-muted)' }}>{acc.email}</div>
                                            </td>
                                            <td style={{ padding: '12px 8px', fontSize: 13, whiteSpace: 'nowrap' }}>
                                                {acc.created_at ? format(new Date(acc.created_at), 'MMM d, yyyy') : 'N/A'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Settings Tab Component
function SettingsTab() {
    const { showAlert } = useAuth();
    const [requireApproval, setRequireApproval] = useState(true);
    const [maintenanceMode, setMaintenanceMode] = useState(false);
    const [adminEmail, setAdminEmail] = useState('admin@atam.sg');
    const [newPassword, setNewPassword] = useState('');

    const handleSaveGeneral = () => {
        // Mock save logic
        showAlert('Settings Saved', 'Global platform settings have been successfully updated.', 'success');
    };

    const handleUpdateProfile = () => {
        // Mock profile update logic
        if (newPassword && newPassword.length < 6) {
            showAlert('Validation Error', 'New password must be at least 6 characters.', 'error');
            return;
        }
        showAlert('Profile Updated', 'Administrator profile details were successfully updated.', 'success');
        setNewPassword('');
    };

    const ToggleSwitch = ({ checked, onChange }) => (
        <label style={{
            position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer'
        }}>
            <input type="checkbox" checked={checked} onChange={onChange} style={{ opacity: 0, width: 0, height: 0 }} />
            <span style={{
                position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: checked ? 'var(--primary)' : 'var(--bg-secondary)',
                transition: '.3s', borderRadius: '34px',
                border: checked ? 'none' : '1px solid var(--border)',
                boxShadow: checked ? '0 0 10px rgba(79,70,229,0.3)' : 'none'
            }}>
                <span style={{
                    position: 'absolute', content: '""', height: '18px', width: '18px',
                    left: checked ? '22px' : '3px', bottom: checked ? '3px' : '2px', backgroundColor: 'white',
                    transition: '.3s', borderRadius: '50%', boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }} />
            </span>
        </label>
    );

    return (
        <div>
            <div className="admin-page-header">
                <h1 className="admin-page-title">Platform Settings</h1>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '24px' }}>
                <div className="admin-card-premium">
                    <h3 style={{ fontSize: '1.2rem', fontWeight: 600, marginBottom: '16px', color: 'var(--admin-text-main)' }}>General Settings</h3>
                    <p style={{ color: 'var(--admin-text-muted)', marginBottom: '24px' }}>Configure global platform behavior.</p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div>
                                <div style={{ fontWeight: 600 }}>Require Event Approval</div>
                                <div style={{ fontSize: '0.85rem', color: 'var(--admin-text-muted)' }}>Admin must approve partner events</div>
                            </div>
                            <ToggleSwitch checked={requireApproval} onChange={(e) => setRequireApproval(e.target.checked)} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div>
                                <div style={{ fontWeight: 600 }}>Maintenance Mode</div>
                                <div style={{ fontSize: '0.85rem', color: 'var(--admin-text-muted)' }}>Temporarily disable public access</div>
                            </div>
                            <ToggleSwitch checked={maintenanceMode} onChange={(e) => setMaintenanceMode(e.target.checked)} />
                        </div>
                    </div>

                    <button
                        onClick={handleSaveGeneral}
                        style={{ 
                            width: '100%', marginTop: '32px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                            padding: '10px 20px', borderRadius: '12px',
                            background: 'var(--v2-primary)', border: 'none',
                            color: 'white', fontWeight: '600', fontSize: '14px',
                            cursor: 'pointer', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)'
                        }}
                    >
                        Save General Settings
                    </button>
                </div>

                <div className="admin-card-premium">
                    <h3 style={{ fontSize: '1.2rem', fontWeight: 600, marginBottom: '16px', color: 'var(--admin-text-main)' }}>Admin Profile</h3>
                    <p style={{ color: 'var(--admin-text-muted)', marginBottom: '24px' }}>Update your administrator details.</p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div>
                            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Admin Email</label>
                            <input type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} className="form-input" style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border)' }} />
                        </div>
                        <div>
                            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>New Password</label>
                            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Leave blank to keep current" className="form-input" style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border)' }} />
                        </div>
                    </div>

                    <button
                        onClick={handleUpdateProfile}
                        style={{ 
                            width: '100%', marginTop: '32px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                            padding: '10px 20px', borderRadius: '12px',
                            background: 'white', border: '1px solid var(--v2-border)',
                            color: 'var(--v2-text-main)', fontWeight: '600', fontSize: '14px',
                            cursor: 'pointer', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                        }}
                    >
                        Update Profile
                    </button>
                </div>
            </div>
        </div>
    );
}
